export interface BuildInfo {
  version: string;
  buildId: string;
  environment: string;
  deployVersion: string;
  bundleHash: string | undefined;
}

// `typeof X` is the only operator in JS that does NOT throw ReferenceError
// when X is unbound. Vite's `define` replaces each identifier with a string
// literal in production (so `typeof "1.2.3" === "string"` and the truthy
// branch wins). Test environments that import this module without first
// stubbing the global get the fallback instead of a hard ReferenceError —
// see telemetryService.test.ts / buildInfo.test.ts for the canonical
// stubbing pattern when a test cares about the actual value.
//
// All four build-time globals plus the bundle hash are immutable for the
// lifetime of the loaded SPA, so memoize the assembled object and reuse it
// for every caller (avoids re-walking <script src> tags on every render or
// telemetry call). Cleared by `__resetBuildInfoCacheForTests` so test suites
// can exercise re-detection.
let cachedBuildInfo: BuildInfo | undefined;

export const getBuildInfo = (): BuildInfo => {
  if (cachedBuildInfo) return cachedBuildInfo;
  cachedBuildInfo = {
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    buildId: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'local',
    environment: typeof __BUILD_ENVIRONMENT__ !== 'undefined' ? __BUILD_ENVIRONMENT__ : 'unknown',
    deployVersion: typeof __DEPLOY_VERSION__ !== 'undefined' ? __DEPLOY_VERSION__ : 'local',
    bundleHash: detectBundleHash(),
  };
  return cachedBuildInfo;
};

// Test-only escape hatch: lets buildInfo.test.ts re-stub globals or inject
// new <script> tags between cases without carrying stale memoized values.
export const __resetBuildInfoCacheForTests = (): void => {
  cachedBuildInfo = undefined;
};

/**
 * Returns the Vite-generated content hash of the entry bundle currently
 * running in the document (e.g. "BWQ8MtsE" from "/assets/index-BWQ8MtsE.js").
 *
 * The same hash also appears in the URL the browser fetched to load this
 * code, so it uniquely identifies the deployed artifact regardless of which
 * ACA revision served it. Used by telemetry to slice events by build during
 * a rollout where two builds are serving traffic simultaneously.
 *
 * Returns undefined in non-DOM contexts (SSR, tests, workers) or when the
 * script-tag pattern doesn't match (local Vite HMR uses module-graph paths
 * without a content hash).
 */
function detectBundleHash(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const scripts = document.querySelectorAll('script[src]');
  for (let i = 0; i < scripts.length; i++) {
    const src = (scripts[i] as HTMLScriptElement).src;
    const m = src.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js(?:[?#]|$)/);
    if (m) return m[1];
  }
  return undefined;
}

export const formatVersionLabel = (info: BuildInfo): string => {
  const buildSuffix = info.buildId !== 'local' ? ` · Build ${info.buildId}` : '';
  return `v${info.version}${buildSuffix}`;
};

export const formatBuildInfoForClipboard = (info: BuildInfo): string =>
  [
    'Auto Portal',
    `Version: ${info.version}`,
    `Build: ${info.buildId}`,
    `Environment: ${info.environment}`,
  ].join('\n');

export const copyBuildInfoToClipboard = async (): Promise<boolean> => {
  try {
    const text = formatBuildInfoForClipboard(getBuildInfo());
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
